import { useId, useLayoutEffect, useRef } from "react";
import { DEMO_DURATION, DEMO_MEAL, easeOut, getDemoScene, progress } from "./demoScenario";
import { DemoScreens } from "./DemoScreens";
import { useContentReveal } from "./useContentReveal";
import { useDemoPlayback } from "./useDemoPlayback";
import { FEATURE_TOURS, FRIDGE_PHOTO, type DemoFeature } from "./featureScenarios";
import "./product-demo.css";

export default function NomsyProductDemo({ feature, compact = false }: { feature?: DemoFeature; compact?: boolean }) {
  const descriptionId = useId();
  const tour = feature ? FEATURE_TOURS[feature] : undefined;
  const duration = tour?.duration ?? DEMO_DURATION;
  const player = useDemoPlayback(duration);
  const time = player.reducedMotion && tour ? tour.stillTime : player.time;
  const scene = tour ? tour.scenes.find((item) => time >= item.start && time < item.end) ?? tour.scenes[0] : getDemoScene(time);
  const screenRef = useRef<HTMLDivElement>(null);
  useContentReveal(screenRef, scene, time, player.reducedMotion);
  useLayoutEffect(() => {
    if (player.reducedMotion) return;
    // One relevant surface per screen keeps quiet holds alive without rushing reading.
    const surface = screenRef.current?.querySelector(
      scene.screen === "menu" ? ".nd-action-menu" :
      scene.screen === "log" ? ".nd-log-entry" :
      scene.screen === "prefill" ? ".nd-voice-canvas" :
      scene.screen === "menu-result" ? ".nd-menu-customize, .nd-menu-recommendation" :
      scene.screen === "fridge-result" ? ".nd-fridge-recommendation" :
      scene.screen === "nutrient-status" ? ".nd-nutrient-card" :
      ".nd-card, .nd-recommendation-card"
    );
    surface?.classList.add("nd-reading-motion");
    return () => surface?.classList.remove("nd-reading-motion");
  }, [scene.id, scene.screen, player.reducedMotion]);
  const entering = player.reducedMotion || scene.start === 0 || ["nutrient-status", "menu-result"].includes(scene.screen) ? 1 : easeOut(progress(time, scene.start, 360));
  const ending = player.reducedMotion ? 1 : 1 - progress(time, duration - 350, 350);
  const screenStyle = { opacity: entering * ending, transform: `translateY(${(1 - entering) * 7}px)` };

  return <div ref={player.stageRef} className={`nd-demo ${compact ? "nd-compact-demo" : feature ? "nd-feature-demo" : ""}`} role="region" aria-label={tour?.label ?? "Nomsy animated product tour"} aria-describedby={descriptionId}
    data-testid={feature ? `nomsy-${feature}-demo` : "nomsy-product-demo"} data-scene={scene.id} data-playing={player.running}>
    <p className="sr-only" id={descriptionId}>{tour?.description ?? "An example day in Nomsy: photograph a salmon poke bowl, review its ingredients, save it to your Food Log, and revisit its ingredients. Your Overview shows a protein gap and suggests 150 g chicken breast. After eating, review and log the suggestion to update your nutrient status."}</p>
    {compact ? <div ref={screenRef} className="nd-screen-content nd-compact-content" style={screenStyle} aria-hidden="true"><DemoScreens scene={scene} time={time} still={player.reducedMotion} /></div> : <div className="nd-phone" aria-hidden="true">
      <div className="nd-phone-screen">
        <div className={`nd-status-bar ${scene.screen === "camera" ? "nd-status-light" : feature && ["nutrient-status", "fridge-analysis", "fridge-result", "menu-analysis", "menu-result"].includes(scene.screen) ? "nd-status-on-gradient" : ""}`}><span>9:41</span><span className="nd-island" /><span className="nd-status-icons">▮▮▮ <span>◒</span> ▰</span></div>
        <div ref={screenRef} className="nd-screen-content" style={screenStyle}><DemoScreens scene={scene} time={time} still={player.reducedMotion} /></div>
        <span className={`nd-home-indicator ${scene.screen === "camera" ? "nd-home-light" : ""}`} />
      </div>
      <img className="nd-phone-outline" src="/images/iphone-outline.png" alt="" draggable={false} />
    </div>
    }
    <img className="nd-preload" src={feature === "fridge-guidance" ? FRIDGE_PHOTO : DEMO_MEAL.photo} alt="" aria-hidden="true" onLoad={() => player.setImageReady(true)} onError={() => player.setImageReady(true)} />
  </div>;
}
